import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Divider, Typography, useMediaQuery, useTheme } from '@mui/material'
import clsx from 'clsx'

import { ErrorResponse, InviteColleaguesForm, useSalutationOptions } from '~api/models'
import { UserApi } from '~api/instances'
import { isDebugEnv } from '~config/envConfig'
import GridContainer from '~shared/components/MuiGrid/GridContainer'
import { FormContext } from '~shared/components/reactHookForm/FormContext'
import { RhfDebugHelper } from '~shared/components/reactHookForm/RhfDebugHelper/RhfDebugHelper'
import Row from '~shared/components/MuiGrid/Row'
import Col from '~shared/components/MuiGrid/Col'
import { useGlobalStyles } from '~shared/styles/globalStyles'
import CircularProgressButton from '~shared/components/ProgressButton/CircularProgressButton'

import { useAddToast } from '~utils/useAddToast'
import { useAutocompleteName } from '~utils/autocompleteType'

import { InviteColleaguesSchema } from './InviteColleagues.schema'
import ScreenContainerMain from '~shared/components/ScreenContainerMain'
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded'
import FormTextFieldNew from '~shared/components/reactHookForm/input/FormTextFieldRedesign'
import FormAutocompleteRedesign from '~shared/components/reactHookForm/input/FormAutocomplete/FormAutocompleteRedesign'

const InviteColleagues: React.FC = () => {
  const gs = useGlobalStyles()
  const resolver = yupResolver<InviteColleaguesForm>(InviteColleaguesSchema)
  const methods = useForm<InviteColleaguesForm>({ resolver })
  const { handleSubmit, reset } = methods
  const addToast = useAddToast()
  const acName = useAutocompleteName<InviteColleaguesForm>()
  const { t } = useTranslation()
  const salutationOptions = useSalutationOptions()
  const [callInProgress, setCallInProgress] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const onSubmit = (data: InviteColleaguesForm) => {
    console.log('Example form submit', data)
    setCallInProgress(true)

    UserApi.inviteColleagues({
      inviteColleaguesForm: data,
    }).then((response) => {
      if (isDebugEnv) console.log('something went right', response)
      reset()
      addToast({ severity: 'success', content: t('common:submitInviteInviteColleagueSuccess') })
    }).catch((error) => {
      if (isDebugEnv) console.log('something went wrong', error)
      try {
        error.json()
          .then((data: ErrorResponse) => {
            switch (data.message) {
              case 'userAlreadyExists':
                addToast({ severity: 'error', content: t('common:submitInviteInviteColleagueFailure_UserExists') })
                break
              default:
                addToast({ severity: 'error', content: t('common:submitInviteInviteColleagueFailure') })
            }
          })
      } catch (e) {
        addToast({ severity: 'error', content: t('common:submitInviteInviteColleagueFailure') })
      }
    }).finally(() => {
      setCallInProgress(false)
    })
  }

  return <ScreenContainerMain>
    <Typography variant='h2' component='h1' className={gs.marginBottomMedium}>
      {t('common:navHeaderInviteColleagues')}
    </Typography>
    <FormContext.Provider value={{ schema: InviteColleaguesSchema }}>
      <FormProvider {...methods}>
        {isDebugEnv && <RhfDebugHelper />}
        <form
          noValidate // disable html validation in favor of yup
          onSubmit={handleSubmit(onSubmit)}
          className={isMobile ? clsx(gs.screenContainerMobile) : clsx(gs.screenContainer)}
        >
          <GridContainer gridSpacing={5} defaultColWidth={6} maxWidth='Sm' roundCornersTop roundCornersBottom>
            <Row className={gs.gridTopMarginFix}>
              <Col xs={12}>
                <Typography variant='h4' component='h2' className={gs.marginTopTiny}>{t('common:navLabelInviteColleagues')}</Typography>
                <Box fontStyle='italic'>
                  <Typography variant='h5' component='h3' className={gs.marginBottomTiny}>
                    {t('common:navDescInviteColleagues')}
                  </Typography>
                  <Divider className={gs.marginBottomSmall} variant='fullWidth' style={{ maxWidth: '125px', backgroundColor: '#000' }} />
                </Box>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormAutocompleteRedesign
                  label={t('common:salutation')}
                  name={acName('salutation')}
                  options={salutationOptions}
                  popupIcon={<KeyboardArrowDownRounded />}
                  fullWidth
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <FormTextFieldNew
                  label={t('common:firstName')}
                  name={acName('firstName')}
                  required
                />
              </Col>
              <Col>
                <FormTextFieldNew
                  label={t('common:surname')}
                  name={acName('lastName')}
                  required
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <FormTextFieldNew
                  label={t('common:eMail')}
                  name={acName('email')}
                  required
                  fullWidth
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <div style={isMobile
                  ? { display: 'flex', width: '85%', justifyContent: 'flex-end' }
                  : { display: 'flex', width: '100%', justifyContent: 'flex-end' }}
                >
                  <CircularProgressButton
                    variant='contained'
                    size='small'
                    color='secondary'
                    type='submit'
                    disabled={callInProgress}
                    disableElevation
                    className={clsx(gs.marginTopSmall, gs.marginBottomSmall)}
                    text={t('form:submitForm')}
                  />
                </div>
              </Col>
            </Row>
          </GridContainer>
        </form>
      </FormProvider>
    </FormContext.Provider>
  </ScreenContainerMain>
}

export default InviteColleagues
